import {
  ChevronDown,
  ChevronUp,
  Pencil,
  Plus,
  Trash2,
  X,
  Zap,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { getDifficultyLabel } from '../../../lib/difficulty.js'
import { Badge } from '../shared/badge'
import { DIFFICULTY_LEVELS, SOLUTION_TYPES } from './constants'
import type {
  CoachingQuestion,
  DifficultyInfo,
  QuestionApproach,
} from './types'

interface ApproachesModalProps {
  isOpen: boolean
  onClose: () => void
  question: CoachingQuestion | null
  onUpdateApproaches: (
    questionId: string,
    approaches: QuestionApproach[],
  ) => Promise<void>
  onEditDetails?: () => void
  allTags: string[]
}

interface EditFormProps {
  data: QuestionApproach
  onChange: (data: QuestionApproach) => void
  onSave: () => void
  onCancel: () => void
  allTags: string[]
}

function EditForm({
  data,
  onChange,
  onSave,
  onCancel,
  allTags,
}: EditFormProps) {
  const handleTagToggle = (tag: string) => {
    const currentTags = data.tags || []
    if (currentTags.includes(tag)) {
      onChange({ ...data, tags: currentTags.filter((t) => t !== tag) })
    } else {
      onChange({ ...data, tags: [...currentTags, tag] })
    }
  }

  return (
    <div className="space-y-4">
      {/* Name */}
      <div>
        <label className="text-muted-foreground mb-2 block text-xs font-medium tracking-wider uppercase">
          Name
        </label>
        <Input
          type="text"
          value={data.name || ''}
          onChange={(e) => onChange({ ...data, name: e.target.value })}
          placeholder="e.g. Two Pointers, Sorting + Binary Search"
        />
      </div>

      {/* Solution Type & Difficulty */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-muted-foreground mb-2 block text-xs font-medium tracking-wider uppercase">
            Solution Type
          </label>
          <div className="flex flex-wrap gap-2">
            {SOLUTION_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => onChange({ ...data, solutionType: type.value })}
                className={cn(
                  'rounded-lg border p-0 transition-colors',
                  data.solutionType === type.value
                    ? 'border-primary'
                    : 'border-transparent hover:border-border',
                )}
              >
                <Badge variant={type.value}>{type.label}</Badge>
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-muted-foreground mb-2 block text-xs font-medium tracking-wider uppercase">
            Difficulty
          </label>
          <div className="flex flex-wrap gap-2">
            {DIFFICULTY_LEVELS.map((level) => (
              <button
                key={level.value}
                type="button"
                onClick={() =>
                  onChange({
                    ...data,
                    difficulty: {
                      name: level.value,
                    } as DifficultyInfo,
                  })
                }
                className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                  data.difficulty?.name === level.value
                    ? `border-current ${level.color} bg-current/10`
                    : 'border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                {level.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tags */}
      {allTags.length > 0 && (
        <div>
          <label className="text-muted-foreground mb-2 block text-xs font-medium tracking-wider uppercase">
            Tags
          </label>
          <div className="border-border bg-background flex max-h-32 flex-wrap gap-2 overflow-auto rounded-lg border p-2">
            {allTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => handleTagToggle(tag)}
                className={`rounded-full border px-2 py-1 text-xs transition-colors ${
                  (data.tags || []).includes(tag)
                    ? 'border-sky-500 bg-sky-500/20 text-sky-400'
                    : 'border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" onClick={onSave}>
          Save Changes
        </Button>
      </div>
    </div>
  )
}

/**
 * Stable identity for an approach card. Server-loaded approaches carry `id`;
 * approaches added in this session carry a client `tempId`. Keying (and
 * tracking expansion) by this instead of the array index stops React from
 * reusing a mid-edit card's local state for a different approach when the
 * array shifts after a delete.
 */
function approachKey(approach: QuestionApproach): string | undefined {
  return approach.id ?? approach.tempId
}

interface ApproachCardProps {
  approach: QuestionApproach
  index: number
  isExpanded: boolean
  onToggle: () => void
  onDelete: (index: number) => void
  onUpdate: (index: number, approach: QuestionApproach) => void
  allTags: string[]
}

function ApproachCard({
  approach,
  index,
  isExpanded,
  onToggle,
  onDelete,
  onUpdate,
  allTags,
}: ApproachCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editData, setEditData] = useState(approach)

  const handleSave = () => {
    onUpdate(index, editData)
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditData(approach)
    setIsEditing(false)
  }

  const solutionTypeConfig =
    SOLUTION_TYPES.find((t) => t.value === approach.solutionType) ||
    SOLUTION_TYPES[0]

  return (
    <div className="border-border bg-muted overflow-hidden rounded-lg border">
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className="hover:bg-muted/80 flex w-full items-center justify-between p-3 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-muted-foreground font-mono text-sm">
            #{index}
          </span>
          <Badge variant={approach.solutionType ?? undefined}>
            {solutionTypeConfig.label}
          </Badge>
          {approach.difficulty && (
            <Badge variant={approach.difficulty.name}>
              {getDifficultyLabel(approach.difficulty)}
            </Badge>
          )}
          {approach.tags && approach.tags.length > 0 && (
            <div className="flex gap-1">
              {approach.tags.slice(0, 2).map((tag) => (
                <Badge key={tag} variant="topic" className="text-[10px]">
                  {tag}
                </Badge>
              ))}
              {approach.tags.length > 2 && (
                <span className="text-muted-foreground text-xs">
                  +{approach.tags.length - 2}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {approach.name && (
            <span className="text-muted-foreground max-w-[200px] truncate text-sm">
              {approach.name}
            </span>
          )}
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-border space-y-4 border-t p-4">
          {isEditing ? (
            <EditForm
              data={editData}
              onChange={setEditData}
              onSave={handleSave}
              onCancel={handleCancel}
              allTags={allTags}
            />
          ) : (
            <>
              {/* Name */}
              {approach.name && (
                <div>
                  <h4 className="text-muted-foreground mb-1 text-xs font-medium tracking-wider uppercase">
                    Name
                  </h4>
                  <p className="text-foreground text-sm">{approach.name}</p>
                </div>
              )}

              {/* Tags */}
              {approach.tags && approach.tags.length > 0 && (
                <div>
                  <h4 className="text-muted-foreground mb-2 text-xs font-medium tracking-wider uppercase">
                    Tags
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {approach.tags.map((tag) => (
                      <Badge key={tag} variant="topic">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:bg-destructive/10"
                  onClick={() => onDelete(index)}
                >
                  <Trash2 size={14} className="mr-1" />
                  Delete
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function AddApproachForm({
  onAdd,
  onCancel,
  allTags,
}: {
  onAdd: (approach: QuestionApproach) => void
  onCancel: () => void
  allTags: string[]
}) {
  const [data, setData] = useState<QuestionApproach>({
    name: '',
    solutionType: 'acceptable',
    difficulty: null,
    strategies: [],
    tags: [],
  })

  const handleSubmit = () => {
    onAdd(data)
  }

  return (
    <div className="border-primary bg-primary/5 rounded-lg border p-4">
      <h3 className="text-foreground mb-4 flex items-center gap-2 text-sm font-medium">
        <Plus size={16} className="text-primary" />
        Add New Approach
      </h3>
      <EditForm
        data={data}
        onChange={setData}
        onSave={handleSubmit}
        onCancel={onCancel}
        allTags={allTags}
      />
    </div>
  )
}

export function ApproachesModal({
  isOpen,
  onClose,
  question,
  onUpdateApproaches,
  onEditDetails,
  allTags,
}: ApproachesModalProps) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  const [isAdding, setIsAdding] = useState(false)

  const approaches = question?.approaches || []

  const handleAddApproach = async (newApproach: QuestionApproach) => {
    if (!question) return
    // Give the new approach a stable client id so its card keeps its own
    // identity until a server id replaces it on save.
    const updated = [
      ...approaches,
      { ...newApproach, tempId: `temp-${crypto.randomUUID()}` },
    ]
    await onUpdateApproaches(question.id, updated)
    setIsAdding(false)
  }

  const handleUpdateApproach = async (
    index: number,
    updatedApproach: QuestionApproach,
  ) => {
    if (!question) return
    const updated = [...approaches]
    updated[index] = updatedApproach
    await onUpdateApproaches(question.id, updated)
  }

  const handleDeleteApproach = async (index: number) => {
    if (!question) return
    const deletedKey = approachKey(approaches[index])
    const updated = approaches.filter((_, i) => i !== index)
    await onUpdateApproaches(question.id, updated)
    if (deletedKey !== undefined && expandedKey === deletedKey) {
      setExpandedKey(null)
    }
  }

  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div
        className="bg-card border-border relative z-10 max-h-[85vh] w-full max-w-2xl overflow-hidden rounded-xl border shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-border bg-muted flex items-center justify-between border-b p-4">
          <div>
            <h2 className="text-foreground text-lg font-semibold">
              Approaches
            </h2>
            <p className="text-muted-foreground mt-0.5 text-sm">
              {question?.name}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {onEditDetails && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onEditDetails}
                className="gap-1"
              >
                <Pencil size={14} />
                Edit Details
              </Button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg p-2 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="max-h-[calc(85vh-140px)] space-y-3 overflow-auto p-4">
          {approaches.length === 0 && !isAdding ? (
            <div className="py-8 text-center">
              <div className="bg-muted mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full">
                <Zap size={24} className="text-muted-foreground" />
              </div>
              <p className="text-muted-foreground mb-4">
                No approaches documented yet.
              </p>
              <Button onClick={() => setIsAdding(true)}>
                <Plus size={16} className="mr-2" />
                Add First Approach
              </Button>
            </div>
          ) : (
            <>
              {approaches.map((approach, index) => {
                const key = approachKey(approach)
                const isExpanded = key !== undefined && expandedKey === key
                return (
                  <ApproachCard
                    key={key ?? `approach-${index}`}
                    approach={approach}
                    index={index}
                    isExpanded={isExpanded}
                    onToggle={() =>
                      setExpandedKey(isExpanded ? null : (key ?? null))
                    }
                    onDelete={handleDeleteApproach}
                    onUpdate={handleUpdateApproach}
                    allTags={allTags}
                  />
                )
              })}

              {isAdding && (
                <AddApproachForm
                  onAdd={handleAddApproach}
                  onCancel={() => setIsAdding(false)}
                  allTags={allTags}
                />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {(approaches.length > 0 || isAdding) && !isAdding && (
          <div className="border-border bg-muted border-t p-4">
            <button
              type="button"
              onClick={() => setIsAdding(true)}
              className="border-border text-muted-foreground hover:text-foreground hover:border-primary hover:bg-primary/5 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-2 transition-colors"
            >
              <Plus size={16} />
              Add Another Approach
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
